import React, { useState } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, CheckCircle2, Mail } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  fr: {
    title: "Contactez-nous",
    intro: "Une question, un bug, une demande commerciale ou liée à vos données personnelles ? Remplissez le formulaire ci-dessous, nous répondons sous 1 à 2 jours ouvrés.",
    name: "Nom",
    email: "Adresse email",
    type: "Type de demande",
    message: "Votre message",
    send: "Envoyer",
    sending: "Envoi…",
    success: "Message envoyé !",
    successDetail: "Nous avons bien reçu votre message et vous avons envoyé un accusé de réception. À très vite.",
    sendAnother: "Envoyer un autre message",
    types: [
      { v: "bug", l: "Bug / souci technique" },
      { v: "sales", l: "Question commerciale" },
      { v: "gdpr", l: "Suppression de données (RGPD)" },
      { v: "other", l: "Autre" },
    ],
  },
  en: {
    title: "Contact us",
    intro: "A question, a bug, a sales inquiry, or a personal-data request? Fill out the form below — we respond within 1-2 business days.",
    name: "Name",
    email: "Email address",
    type: "Inquiry type",
    message: "Your message",
    send: "Send",
    sending: "Sending…",
    success: "Message sent!",
    successDetail: "We received your message and sent you an acknowledgement. We'll be in touch soon.",
    sendAnother: "Send another message",
    types: [
      { v: "bug", l: "Bug / technical issue" },
      { v: "sales", l: "Sales inquiry" },
      { v: "gdpr", l: "Data deletion (GDPR)" },
      { v: "other", l: "Other" },
    ],
  },
};

export default function Contact() {
  const { lang } = useI18n();
  const L = LABELS[lang] || LABELS.fr;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState("other");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !message) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND}/api/contact`, {
        name, email, inquiry_type: type, message, locale: lang, _hp_field: hp,
      });
      setDone(true);
      setName(""); setEmail(""); setType("other"); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "en" ? "Failed to send" : "Échec de l'envoi"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="App min-h-screen bg-background" data-testid="contact-page">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {lang === "en" ? "Contact" : "Contact"}</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">{L.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{L.intro}</p>

        {done ? (
          <div className="mt-10 border border-emerald-500 bg-emerald-50 p-6" data-testid="contact-success">
            <div className="flex items-center gap-2 font-mono text-xs uppercase text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> {L.success}
            </div>
            <p className="mt-3 text-sm text-emerald-900">{L.successDetail}</p>
            <Button onClick={() => setDone(false)} variant="outline" className="mt-4 rounded-sm gap-2">
              <Mail className="h-4 w-4" /> {L.sendAnother}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">{L.name}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" data-testid="contact-name" />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">{L.email}*</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="contact-email" />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase text-muted-foreground">{L.type}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="contact-type"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-sm">
                  {L.types.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase text-muted-foreground">{L.message}*</Label>
              <Textarea required rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 rounded-sm" data-testid="contact-message" />
            </div>
            {/* Honeypot: invisible field that legitimate users never fill */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={sending} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="contact-send">
                <Send className="h-4 w-4" /> {sending ? L.sending : L.send}
              </Button>
            </div>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
